import React, { useEffect, useState } from 'react';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { ProgressCircular } from "@americanexpress/dls-react";
import { MODULE_NAME, MOSAIC_GET_INSPIRED_EXPERIMENT } from "../constants/moduleConstants";
import { mockHeading, mockArticleSets } from '../../__data__/mockArticleData';
import type { MockArticleSetKey, TravelPersonalizedMarketingArticlesType } from "../types/PersonalizedMarketingArticles";
import { loadModuleData } from "../thunks/loadModuleData";
import { Experiment, Variant } from '@americanexpress/one-experiments';
import { ArticleLayout } from "./ArticleLayout/ArticleLayout";
import styles from "./MarketingArticleStyles.css";
import '@americanexpress/consumer-travel-base/build/index.css';
import { IntlProvider } from 'react-intl';
import { useArticles } from '../hooks/useArticles';
import { filterArticles } from '../utils/articles';

const PersonalizedMarketingArticles: TravelPersonalizedMarketingArticlesType = ({
  languageData,
  heading,
  articles,
  isParentDataLoading,
  useMockData = false,
  analyticsElementPrefix,
  clientId,
  placementId,
}) => {
  /* istanbul ignore next - used for local testing on demo route only */
  const [selectedMockSet, setSelectedMockSet] = useState<MockArticleSetKey>('Four Articles');
  /* istanbul ignore next - local testing */
  heading = useMockData ? mockHeading : heading;
  /* istanbul ignore next - local testing */
  articles = filterArticles(useMockData ? mockArticleSets[selectedMockSet] : articles);

  const {
    isLoading: oneDataArticlesLoading,
  } = useArticles({
    clientId,
    placementId,
  });

  // TODO: talk about error states, and error logging with the wider team
  /* istanbul ignore next - local testing */
  const localTestingComponent = useMockData ? (
    <div className="flex flex-justify-center pad-t">
      <label htmlFor="mock-articles-select">Mock article set:</label>
      <select
        id="mock-articles-select"
        value={selectedMockSet}
        onChange={(event) => setSelectedMockSet(event.target.value as MockArticleSetKey)}
      >
        {Object.keys(mockArticleSets).map((key) => <option key={key} value={key}>{key}</option>)}
      </select>
    </div>
  ) : null;

  if (isParentDataLoading || oneDataArticlesLoading) {
    return (
      <IntlProvider locale={languageData.locale} messages={languageData} >
        <div className={`container flex flex-justify-center flex-align-center ${styles.progressWrapper}`}>
          <ProgressCircular size="lg"/>
        </div>
      </IntlProvider>
    );
  }

  return (
    <IntlProvider locale={languageData.locale} messages={languageData} >
      <div data-testid="personalized-marketing-articles" className={`container ${styles.marketingArticlesWrapper}`}>

      <Experiment
        experimentName={MOSAIC_GET_INSPIRED_EXPERIMENT}
        defaultVariant="control"
      >
        <Variant variant="control">
          <ArticleLayout
            heading={heading}
            analyticsElementPrefix={analyticsElementPrefix} 
            clientId={clientId}
            placementId={placementId}
            articles={articles}
          />
        </Variant>

        <Variant variant="user">
          <ArticleLayout
            heading={heading}
            analyticsElementPrefix={analyticsElementPrefix} 
            clientId={clientId}
            placementId={placementId}
            articlesOverrideKey="recommendedForYou"
            articles={articles}
          />
        </Variant>
      </Experiment>

      </div>
      { localTestingComponent }
    </IntlProvider>
  )
};

export const TestableTravelPersonalizedMarketingArticles = PersonalizedMarketingArticles;

PersonalizedMarketingArticles.holocron = {
  name: MODULE_NAME,
  loadModuleData,
};

export default oneAppModuleWrapper(MODULE_NAME)(PersonalizedMarketingArticles);
